// src/components/settings/catalog/CatalogItemEditor.js
// CATALOG SCREEN VERIFIER: catscreen-7a2c4e91 — Settings › Catalog, Stage 2.
//
// The item editor (a side panel on wider screens; under the list on a phone). Saves ONLY through
// the catalog callables: saveCatalogItem (fields, price, cost, Keep my markup, suppliers, and the
// folders of a NEW item in the same save), setCatalogItemFolders (an existing item's folders),
// setCatalogItemArchived, saveCatalogSupplier ("+ New supplier" here).
//
// Price box: Cost / Markup % / Price work both ways. Typing a markup sets the price; typing a
// price shows its markup. Changing the cost (or the default supplier's cost) moves the price to
// keep the markup when Keep my markup is on — previewPrice is the server's applyCostChange, so the
// number shown is the number saved. A price typed in this edit always wins (as on the server).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, Lock, ChevronDown, ChevronRight, Loader2, Star, Trash2, History } from 'lucide-react';
import {
    UNITS, TYPES, TYPE_HELP, LIMITS, parseMoney, deriveMarkup, previewPrice, priceFromMarkup, formatMoney, formatPct,
    moneyText, effectiveCost, findDraftProblem, draftCost, buildSaveInput, flatFolders, folderPath, historyCause, historyChanges,
    itemTaxCodeChoices, ITEM_TAX_CODE_HINT, // taxpick4-54573069
} from '../../../utils/catalogUi';
import { getQuickBooksMappingOptionsCached } from '../../../utils/qbMappingCache';
import {
    loadItem, loadHistory, saveCatalogItem, setCatalogItemFolders, setCatalogItemArchived, saveCatalogSupplier,
    friendlyError, shortDate, dateTime,
} from './catalogApi';
import { BTN_GHOST, BTN_PRIMARY, BTN_DANGER, BTN_LINK, INPUT, LABEL, Req, Opt, ErrorLine } from './catalogUiParts';

const SAVE_ERROR = "Couldn't save the item. Try again.";

const emptyDraft = (folderId) => ({
    name: '', type: 'Service', unit: 'Unit', taxCodeId: '', salesTaxCodeId: '', description: '', partNumber: '', manufacturer: '', barcode: '',
    costText: '', priceText: '', keepMarkup: true, suppliers: [], folderIds: folderId ? [folderId] : [],
});

function fromLoaded(item, cost) {
    const suppliers = cost && Array.isArray(cost.suppliers) ? cost.suppliers : [];
    const fields = {
        name: item.name || '', type: item.type || 'Service', unit: item.unit || 'Unit', description: item.description || '',
        partNumber: item.partNumber || null, manufacturer: item.manufacturer || null, barcode: item.barcode || null, taxCodeId: item.taxCodeId || null,
        salesTaxCodeId: item.salesTaxCodeId || null, // taxpick4-54573069
    };
    const original = {
        fields, price: typeof item.price === 'number' ? item.price : null,
        cost: cost ? effectiveCost(suppliers, cost.cost) : null,
        keepMarkup: cost ? cost.keepMarkup !== false : true,
        suppliers: suppliers.map((s) => ({ supplierId: s.supplierId, supplierPartNumber: s.supplierPartNumber || null, cost: s.cost, isDefault: s.isDefault === true, costUpdatedAt: s.costUpdatedAt || null })),
        folderIds: Array.isArray(item.folderIds) ? item.folderIds : [],
        archived: item.archived === true,
        accountingLink: item.accountingLink || null,
    };
    const draft = {
        name: fields.name, type: fields.type, unit: fields.unit, taxCodeId: fields.taxCodeId || '', salesTaxCodeId: fields.salesTaxCodeId || '', description: fields.description,
        partNumber: fields.partNumber || '', manufacturer: fields.manufacturer || '', barcode: fields.barcode || '',
        costText: moneyText(original.cost), priceText: moneyText(original.price), keepMarkup: original.keepMarkup,
        suppliers: original.suppliers.map((s) => ({ ...s, supplierPartNumber: s.supplierPartNumber || '', costText: moneyText(s.cost) })),
        folderIds: original.folderIds,
    };
    return { original, draft };
}

export default function CatalogItemEditor({ orgId, target, tree, suppliers, showTax, salesTaxCodes, onClose, onSaved, onArchived, onDirtyChange }) {
    const creating = target.mode === 'new';
    const [loadState, setLoadState] = useState(creating ? 'ready' : 'loading'); // loading | ready | error | gone
    const [original, setOriginal] = useState(null);
    const [draft, setDraft] = useState(() => emptyDraft(target.folderId));
    const [priceTouched, setPriceTouched] = useState(false);
    const [markupText, setMarkupText] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [moreOpen, setMoreOpen] = useState(false);
    const nameRef = useRef(null);
    const salesTax = !showTax && Array.isArray(salesTaxCodes); // taxpick4-54573069 — the Settings › Taxes "Tax code" box

    const load = async () => {
        setLoadState('loading');
        try {
            const { item, cost } = await loadItem(orgId, target.id);
            if (!item) { setLoadState('gone'); return; }
            const r = fromLoaded(item, cost);
            setOriginal(r.original);
            setDraft(r.draft);
            setPriceTouched(false);
            setMarkupText(null);
            setMoreOpen(!!r.draft.barcode);
            setLoadState('ready');
        } catch (err) {
            console.error('Catalog: could not load item', err);
            setLoadState('error');
        }
    };
    useEffect(() => { if (!creating) load(); }, [target.id]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { if (creating && nameRef.current) nameRef.current.focus(); }, [creating]);

    const supplierName = (id) => { const s = suppliers.find((x) => x.id === id); return s ? s.name : 'Supplier'; };
    const set = (patch) => { setError(''); setDraft((d) => ({ ...d, ...patch })); };

    // ── the price box ──
    const cost = draftCost(draft);
    const typed = creating || priceTouched;
    const typedPrice = typed ? parseMoney(draft.priceText) : undefined;
    const preview = creating || !original
        ? { price: typeof typedPrice === 'number' ? typedPrice : null, priceMovedBy: 'typed', note: null }
        : previewPrice({ oldCost: original.cost, oldPrice: original.price, newCost: cost, keepMarkup: draft.keepMarkup, typedPrice: typed ? (typeof typedPrice === 'number' ? typedPrice : null) : undefined });
    const priceShown = typed ? draft.priceText : moneyText(preview.price);
    const shownPriceNumber = parseMoney(priceShown);
    const derived = deriveMarkup(cost, typeof shownPriceNumber === 'number' ? shownPriceNumber : null);
    const markupShown = markupText !== null ? markupText : (derived.markupPct === null ? '' : String(derived.markupPct));
    const canMarkup = typeof cost === 'number' && cost > 0;

    const onCost = (text) => { setMarkupText(null); set({ costText: text }); };
    const onPrice = (text) => { setPriceTouched(true); setMarkupText(null); set({ priceText: text }); };
    const onMarkup = (text) => {
        setMarkupText(text);
        const m = parseMoney(text);
        if (typeof m === 'number' && canMarkup) { setPriceTouched(true); set({ priceText: moneyText(priceFromMarkup(cost, m)) }); }
    };

    // ── suppliers ──
    const [addSupplierId, setAddSupplierId] = useState('');
    const [newSupplier, setNewSupplier] = useState(null); // null | { name, saving, error }
    const linked = new Set(draft.suppliers.map((s) => s.supplierId));
    const addable = suppliers.filter((s) => s.archived !== true && !linked.has(s.id));
    const addSupplier = (supplierId) => {
        if (!supplierId) return;
        setMarkupText(null);
        setDraft((d) => {
            const first = d.suppliers.length === 0;
            // The first supplier starts at the item's current cost, so adding it doesn't change the cost by itself.
            return { ...d, suppliers: [...d.suppliers, { supplierId, supplierPartNumber: '', costText: first ? d.costText : '', isDefault: first, costUpdatedAt: null }] };
        });
        setAddSupplierId('');
    };
    const createSupplier = async () => {
        const name = newSupplier.name.trim();
        if (!name) { setNewSupplier((n) => ({ ...n, error: 'Please give the supplier a name.' })); return; }
        setNewSupplier((n) => ({ ...n, saving: true, error: '' }));
        try {
            const r = await saveCatalogSupplier({ name });
            addSupplier(r.supplierId);
            setNewSupplier(null);
        } catch (err) {
            setNewSupplier((n) => ({ ...n, saving: false, error: friendlyError(err, "Couldn't add the supplier. Try again.") }));
        }
    };
    const patchSupplier = (id, patch) => { setMarkupText(null); set({ suppliers: draft.suppliers.map((s) => (s.supplierId === id ? { ...s, ...patch } : s)) }); };
    const makeDefault = (id) => { setMarkupText(null); set({ suppliers: draft.suppliers.map((s) => ({ ...s, isDefault: s.supplierId === id })) }); };
    const removeSupplier = (id) => {
        setMarkupText(null);
        const was = draft.suppliers.find((s) => s.supplierId === id);
        let rest = draft.suppliers.filter((s) => s.supplierId !== id);
        if (was && was.isDefault && rest.length) rest = rest.map((s, i) => ({ ...s, isDefault: i === 0 }));
        // No suppliers left: the item keeps its last cost, now typed directly (as the server does).
        set(rest.length ? { suppliers: rest } : { suppliers: [], costText: moneyText(cost) });
    };

    // ── folders ──
    const allFolders = useMemo(() => flatFolders(tree), [tree]);
    const [addFolderId, setAddFolderId] = useState('');
    const folderChoices = allFolders.filter((f) => !draft.folderIds.includes(f.id));

    // ── QuickBooks tax codes ──
    const [tax, setTax] = useState({ loading: showTax, codes: [], error: '' });
    useEffect(() => {
        if (!showTax) return undefined;
        let alive = true;
        getQuickBooksMappingOptionsCached(orgId)
            .then((d) => { if (alive) setTax({ loading: false, codes: (d && d.taxCodes) || [], error: '' }); })
            .catch(() => { if (alive) setTax({ loading: false, codes: [], error: "Couldn't load your QuickBooks tax codes. The item keeps the one it has." }); });
        return () => { alive = false; };
    }, [orgId, showTax]);

    // ── price history ──
    const [hist, setHist] = useState({ open: false, entries: [], last: null, loading: false, error: '' });
    const loadMoreHistory = async (reset) => {
        setHist((h) => ({ ...h, open: true, loading: true, error: '', ...(reset ? { entries: [], last: null } : {}) }));
        try {
            const r = await loadHistory(orgId, target.id, reset ? null : hist.last);
            setHist((h) => ({ ...h, loading: false, entries: (reset ? [] : h.entries).concat(r.entries), last: r.last }));
        } catch (err) {
            console.error('Catalog: could not load price history', err);
            setHist((h) => ({ ...h, loading: false, error: "Couldn't load the price history. Try again." }));
        }
    };

    // ── dirty, for "discard your changes?" ──
    const pending = useMemo(() => {
        if (creating) return null;
        if (!original || loadState !== 'ready') return null;
        try { return buildSaveInput(draft, original, { creating: false, priceTouched, showTax, salesTax }); } catch (e) { return null; }
    }, [draft, original, priceTouched, showTax, salesTax, creating, loadState]);
    const dirty = creating
        ? !!(draft.name.trim() || draft.priceText || draft.costText || draft.description.trim() || draft.suppliers.length)
        : !!pending && (Object.keys(pending.input).length > 0 || pending.foldersChanged);
    useEffect(() => { onDirtyChange(dirty); }, [dirty]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── save / archive ──
    const save = async () => {
        const problem = findDraftProblem(draft, { creating });
        if (problem) { setError(problem); return; }
        const { input, folderIds, foldersChanged } = buildSaveInput(draft, original, { creating, priceTouched, showTax, salesTax });
        setSaving(true);
        setError('');
        let res = null;
        let itemId = creating ? null : target.id;
        try {
            if (creating || Object.keys(input).length) res = await saveCatalogItem(creating ? input : { itemId, ...input });
            if (creating) itemId = res.itemId;
        } catch (err) {
            console.error('Catalog: save failed', err);
            setSaving(false);
            setError(friendlyError(err, SAVE_ERROR));
            return;
        }
        if (foldersChanged) {
            try {
                await setCatalogItemFolders(itemId, folderIds);
            } catch (err) {
                console.error('Catalog: folders not saved', err);
                setSaving(false);
                // Saving again is safe: the item part sends the same values (no new price history).
                setError(res ? `The item was saved, but its folders weren't changed. ${friendlyError(err, 'Press Save to try again.')}` : friendlyError(err, SAVE_ERROR));
                return;
            }
        }
        setSaving(false);
        if (!res && !foldersChanged) { onClose({ force: true, notice: 'No changes to save.' }); return; }
        const def = draft.suppliers.find((s) => s.isDefault);
        onSaved({
            id: itemId,
            name: draft.name.trim(), type: draft.type, unit: draft.unit,
            price: res ? res.price : preview.price,
            cost: res ? res.cost : cost,
            markupPct: res ? res.markupPct : derived.markupPct,
            partNumber: draft.partNumber.trim() || null, manufacturer: draft.manufacturer.trim() || null, barcode: draft.barcode.trim() || null,
            folderIds,
            archived: original ? original.archived : false,
            defaultSupplierId: def ? def.supplierId : null,
            defaultSupplierName: def ? supplierName(def.supplierId) : null,
            supplierCount: draft.suppliers.length,
        }, { created: creating });
    };

    const toggleArchive = async () => {
        setSaving(true);
        setError('');
        try {
            await setCatalogItemArchived(target.id, !original.archived);
            setSaving(false);
            onArchived(target.id, !original.archived, draft.name.trim() || original.fields.name);
        } catch (err) {
            setSaving(false);
            setError(friendlyError(err, original.archived ? "Couldn't restore the item. Try again." : "Couldn't archive the item. Try again."));
        }
    };

    const title = creating ? 'New item' : (original ? original.fields.name || 'Item' : 'Item');

    return (
        <section aria-label={creating ? 'New item' : `Edit ${title}`} className="flex flex-col h-full bg-white">
            <header className="flex items-center justify-between gap-2 px-4 sm:px-5 py-2 border-b border-gray-200 sticky top-0 bg-white z-10">
                <h3 className="text-lg font-bold text-gray-800 truncate">{creating ? 'New item' : `Edit ${title}`}</h3>
                <button type="button" className="w-11 h-11 inline-flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100" aria-label="Close" onClick={() => onClose({})} disabled={saving}>
                    <X size={18} />
                </button>
            </header>

            {loadState === 'loading' && <div className="p-6 text-sm text-gray-500"><Loader2 size={16} className="inline animate-spin mr-2" />Loading the item…</div>}
            {loadState === 'error' && <div className="p-5"><ErrorLine text="Couldn't load this item. Try again." onRetry={load} /></div>}
            {loadState === 'gone' && <div className="p-5"><ErrorLine text="This item no longer exists." /></div>}

            {loadState === 'ready' && (
                <>
                    <div className="flex-1 px-4 sm:px-5 py-4 space-y-5">
                        {original && original.archived && (
                            <p className="text-sm p-3 rounded-lg bg-gray-50 border border-gray-200 text-gray-700">This item is archived. It can't be added to new estimates or invoices until you restore it.</p>
                        )}

                        <div>
                            <label htmlFor="ci-name" className={LABEL}>Name<Req /></label>
                            <input id="ci-name" ref={nameRef} className={INPUT} value={draft.name} maxLength={LIMITS.name} onChange={(e) => set({ name: e.target.value })} />
                        </div>

                        <fieldset>
                            <legend className={LABEL}>Type</legend>
                            <div className="grid grid-cols-3 gap-2">
                                {['Service', 'Part', 'Material'].map((t) => (
                                    <button key={t} type="button" aria-pressed={draft.type === t} onClick={() => set({ type: t })}
                                        className={`min-h-[44px] rounded-lg border text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5F4EC9]/40 ${
                                            draft.type === t ? 'border-[#5F4EC9] bg-[#5F4EC9]/10 text-[#5F4EC9]' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}>
                                        {t}
                                    </button>
                                ))}
                            </div>
                            <p className="text-xs text-gray-500 mt-1.5">{TYPE_HELP[draft.type] || ''}</p>
                            {!TYPES.includes(draft.type) && <p className="text-xs text-amber-700 mt-1">Choose a type.</p>}
                        </fieldset>

                        <div className={`grid gap-3 ${showTax || salesTax ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'}${salesTax ? ' items-end' : ''}`}>
                            <div>
                                <label htmlFor="ci-unit" className={LABEL}>Unit</label>
                                <select id="ci-unit" className={INPUT} value={draft.unit} onChange={(e) => set({ unit: e.target.value })}>
                                    {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                                </select>
                            </div>
                            {showTax && (
                                <div>
                                    <label htmlFor="ci-tax" className={LABEL}>QuickBooks tax code<Opt /></label>
                                    <select id="ci-tax" className={INPUT} value={draft.taxCodeId} disabled={tax.loading || !!tax.error} onChange={(e) => set({ taxCodeId: e.target.value })}>
                                        <option value="">Company default</option>
                                        {tax.codes.map((c) => <option key={c.Id} value={c.Id}>{c.Name}</option>)}
                                        {draft.taxCodeId && !tax.codes.some((c) => c.Id === draft.taxCodeId) && (
                                            <option value={draft.taxCodeId}>{tax.loading ? 'Loading…' : 'Current code (not found in QuickBooks)'}</option>
                                        )}
                                    </select>
                                    {tax.error && <p className="text-xs text-amber-700 mt-1">{tax.error}</p>}
                                </div>
                            )}
                            {salesTax && (
                                <div>
                                    <label htmlFor="ci-sales-tax" className={LABEL}>Tax code<Opt /></label>
                                    <select id="ci-sales-tax" className={INPUT} value={draft.salesTaxCodeId} onChange={(e) => set({ salesTaxCodeId: e.target.value })}>
                                        {itemTaxCodeChoices(salesTaxCodes, draft.salesTaxCodeId).map((o) => <option key={o.value || 'default'} value={o.value}>{o.label}</option>)}
                                    </select>
                                </div>
                            )}
                        </div>
                        {salesTax && <p className="text-xs text-gray-500 -mt-2">{ITEM_TAX_CODE_HINT}</p>}

                        <div>
                            <label htmlFor="ci-desc" className={LABEL}>Description<Opt /></label>
                            <textarea id="ci-desc" className={`${INPUT} py-2`} rows={3} maxLength={LIMITS.description} value={draft.description} onChange={(e) => set({ description: e.target.value })} />
                            <p className="text-xs text-gray-500 mt-1">Prints on estimates and invoices.</p>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label htmlFor="ci-part" className={LABEL}>Your part #<Opt /></label>
                                <input id="ci-part" className={INPUT} maxLength={LIMITS.partNumber} value={draft.partNumber} onChange={(e) => set({ partNumber: e.target.value })} />
                            </div>
                            <div>
                                <label htmlFor="ci-mfr" className={LABEL}>Manufacturer<Opt /></label>
                                <input id="ci-mfr" className={INPUT} maxLength={LIMITS.manufacturer} value={draft.manufacturer} onChange={(e) => set({ manufacturer: e.target.value })} />
                            </div>
                        </div>

                        {/* ── price box ── */}
                        <div className="p-4 rounded-xl border border-gray-200 bg-gray-50 space-y-3">
                            <div className="flex items-center justify-between">
                                <h4 className="text-sm font-semibold text-gray-800">Price and cost</h4>
                                <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 px-2 py-0.5 rounded-full bg-white border border-gray-200"><Lock size={12} /> Office only</span>
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                                <div>
                                    <label htmlFor="ci-cost" className="block text-xs font-medium text-gray-600 mb-1">Cost</label>
                                    <input id="ci-cost" inputMode="decimal" className={`${INPUT} text-right`} placeholder="0.00"
                                        value={draft.suppliers.length ? moneyText(cost) : draft.costText}
                                        disabled={draft.suppliers.length > 0} onChange={(e) => onCost(e.target.value)} />
                                </div>
                                <div>
                                    <label htmlFor="ci-markup" className="block text-xs font-medium text-gray-600 mb-1">Markup %</label>
                                    <input id="ci-markup" inputMode="decimal" className={`${INPUT} text-right`} placeholder={canMarkup ? '0' : '—'}
                                        value={markupShown} disabled={!canMarkup} onChange={(e) => onMarkup(e.target.value)} onBlur={() => setMarkupText(null)} />
                                </div>
                                <div>
                                    <label htmlFor="ci-price" className="block text-xs font-medium text-gray-600 mb-1">Price{creating && <Req />}</label>
                                    <input id="ci-price" inputMode="decimal" className={`${INPUT} text-right font-semibold`} placeholder="0.00"
                                        value={priceShown} onChange={(e) => onPrice(e.target.value)} />
                                </div>
                            </div>
                            {draft.suppliers.length > 0 && (
                                <p className="text-xs text-gray-500">The cost comes from the default supplier{(() => { const d = draft.suppliers.find((s) => s.isDefault); return d ? `, ${supplierName(d.supplierId)}` : ''; })()}. Change it in Suppliers below.</p>
                            )}
                            {!canMarkup && <p className="text-xs text-gray-500">Add a cost above 0 to work out the markup.</p>}
                            {!creating && !typed && preview.priceMovedBy === 'markup' && (
                                <p className="text-xs text-[#5F4EC9]">The price moves from {formatMoney(original.price)} to {formatMoney(preview.price)} to keep your {formatPct(deriveMarkup(original.cost, original.price).markupPct)} markup.</p>
                            )}
                            {!creating && !typed && preview.note === 'no-markup-to-keep' && (
                                <p className="text-xs text-gray-600">There was no cost before, so the price stays the same. Type a new price to change it.</p>
                            )}
                            {!creating && !typed && !draft.keepMarkup && original && cost !== original.cost && (
                                <p className="text-xs text-gray-600">Keep my markup is off, so the price stays the same.</p>
                            )}
                            <div className="flex items-start justify-between gap-3 pt-1">
                                <div>
                                    <p className="text-sm font-medium text-gray-800" id="ci-keep-label">Keep my markup</p>
                                    <p className="text-xs text-gray-500">When the cost changes, the price moves to keep the markup. Turn it off to keep the price as it is.</p>
                                </div>
                                <button type="button" role="switch" aria-checked={draft.keepMarkup} aria-labelledby="ci-keep-label"
                                    onClick={() => set({ keepMarkup: !draft.keepMarkup })}
                                    className="shrink-0 min-h-[44px] min-w-[52px] inline-flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5F4EC9]/40 rounded-full">
                                    <span className={`relative inline-block w-11 h-6 rounded-full transition-colors ${draft.keepMarkup ? 'bg-[#5F4EC9]' : 'bg-gray-300'}`}>
                                        <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${draft.keepMarkup ? 'left-[22px]' : 'left-0.5'}`} />
                                    </span>
                                </button>
                            </div>
                        </div>

                        {/* ── suppliers ── */}
                        {(draft.type !== 'Service' || draft.suppliers.length > 0) && (
                            <div className="space-y-2">
                                <h4 className="text-sm font-semibold text-gray-800">Suppliers</h4>
                                {draft.suppliers.length === 0 && <p className="text-sm text-gray-500">No suppliers. Add one to track where you buy it and what it costs.</p>}
                                {draft.suppliers.map((s) => {
                                    const sup = suppliers.find((x) => x.id === s.supplierId);
                                    const changedCost = !s.costUpdatedAt || parseMoney(s.costText) !== s.cost;
                                    return (
                                        <div key={s.supplierId} className="p-3 rounded-lg border border-gray-200 space-y-2">
                                            <div className="flex items-center justify-between gap-2">
                                                <p className="font-medium text-gray-800 truncate">{sup ? sup.name : 'Supplier'}{sup && sup.archived ? ' (archived)' : ''}</p>
                                                <div className="flex items-center gap-1 shrink-0">
                                                    {s.isDefault
                                                        ? <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-[#5F4EC9]/10 text-[#5F4EC9]"><Star size={12} /> Default</span>
                                                        : <button type="button" className={BTN_LINK} onClick={() => makeDefault(s.supplierId)}>Make default</button>}
                                                    <button type="button" className="w-11 h-11 inline-flex items-center justify-center rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50"
                                                        aria-label={`Remove ${sup ? sup.name : 'this supplier'}`} onClick={() => removeSupplier(s.supplierId)}>
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-2">
                                                <div>
                                                    <label htmlFor={`ci-sp-${s.supplierId}`} className="block text-xs font-medium text-gray-600 mb-1">Supplier part #<Opt /></label>
                                                    <input id={`ci-sp-${s.supplierId}`} className={INPUT} maxLength={LIMITS.supplierPartNumber} value={s.supplierPartNumber}
                                                        onChange={(e) => patchSupplier(s.supplierId, { supplierPartNumber: e.target.value })} />
                                                </div>
                                                <div>
                                                    <label htmlFor={`ci-sc-${s.supplierId}`} className="block text-xs font-medium text-gray-600 mb-1">Cost<Req /></label>
                                                    <input id={`ci-sc-${s.supplierId}`} inputMode="decimal" className={`${INPUT} text-right`} placeholder="0.00" value={s.costText}
                                                        onChange={(e) => patchSupplier(s.supplierId, { costText: e.target.value })} />
                                                </div>
                                            </div>
                                            <p className="text-xs text-gray-400">{changedCost ? 'Cost date updates when you save.' : `Cost updated ${shortDate(s.costUpdatedAt)}`}</p>
                                        </div>
                                    );
                                })}
                                {newSupplier ? (
                                    <div className="p-3 rounded-lg border border-[#5F4EC9]/30 space-y-2">
                                        <label htmlFor="ci-newsup" className={LABEL}>New supplier name<Req /></label>
                                        <input id="ci-newsup" className={INPUT} maxLength={LIMITS.supplierName} value={newSupplier.name} autoFocus
                                            onChange={(e) => setNewSupplier((n) => ({ ...n, name: e.target.value, error: '' }))} />
                                        <ErrorLine text={newSupplier.error} />
                                        <div className="flex gap-2">
                                            <button type="button" className={BTN_PRIMARY} disabled={newSupplier.saving} onClick={createSupplier}>{newSupplier.saving ? 'Adding…' : 'Add supplier'}</button>
                                            <button type="button" className={BTN_GHOST} disabled={newSupplier.saving} onClick={() => setNewSupplier(null)}>Cancel</button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex flex-wrap items-center gap-2">
                                        {addable.length > 0 && (
                                            <>
                                                <label htmlFor="ci-addsup" className="sr-only">Add a supplier</label>
                                                <select id="ci-addsup" className={`${INPUT} w-auto flex-1 min-w-[10rem]`} value={addSupplierId} onChange={(e) => addSupplier(e.target.value)}>
                                                    <option value="">+ Add supplier…</option>
                                                    {addable.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                                                </select>
                                            </>
                                        )}
                                        <button type="button" className={BTN_LINK} onClick={() => setNewSupplier({ name: '', saving: false, error: '' })}>+ New supplier</button>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ── folders ── */}
                        <div className="space-y-2">
                            <h4 className="text-sm font-semibold text-gray-800">Folders</h4>
                            <div className="flex flex-wrap gap-2">
                                {draft.folderIds.length === 0 && <span className="text-sm text-gray-500">Ungrouped</span>}
                                {draft.folderIds.map((id) => (
                                    <span key={id} className="inline-flex items-center gap-1 pl-3 rounded-full bg-[#5F4EC9]/10 text-[#5F4EC9] text-sm">
                                        {folderPath(tree.byId, id) || 'Folder'}
                                        <button type="button" className="w-11 h-11 -my-2 inline-flex items-center justify-center rounded-full hover:bg-[#5F4EC9]/10"
                                            aria-label={`Take it out of ${folderPath(tree.byId, id) || 'this folder'}`} onClick={() => set({ folderIds: draft.folderIds.filter((x) => x !== id) })}>
                                            <X size={14} />
                                        </button>
                                    </span>
                                ))}
                            </div>
                            {folderChoices.length > 0 && (
                                <>
                                    <label htmlFor="ci-addfolder" className="sr-only">Add to folder</label>
                                    <select id="ci-addfolder" className={INPUT} value={addFolderId}
                                        onChange={(e) => { if (e.target.value) set({ folderIds: [...draft.folderIds, e.target.value] }); setAddFolderId(''); }}>
                                        <option value="">+ Add to folder…</option>
                                        {folderChoices.map((f) => <option key={f.id} value={f.id}>{`${'  '.repeat(f.depth)}${f.name}`}</option>)}
                                    </select>
                                </>
                            )}
                            <p className="text-xs text-gray-500">An item can sit in more than one folder.</p>
                        </div>

                        {/* ── more options ── */}
                        <div>
                            <button type="button" className={BTN_LINK} aria-expanded={moreOpen} onClick={() => setMoreOpen((o) => !o)}>
                                {moreOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />} More options
                            </button>
                            {moreOpen && (
                                <div className="mt-2">
                                    <label htmlFor="ci-barcode" className={LABEL}>Barcode / UPC<Opt /></label>
                                    <input id="ci-barcode" className={INPUT} maxLength={LIMITS.barcode} value={draft.barcode} onChange={(e) => set({ barcode: e.target.value })} />
                                    <p className="text-xs text-gray-500 mt-1">Searchable now. Used for scanning when inventory is turned on.</p>
                                </div>
                            )}
                        </div>

                        {showTax && (
                            <p className="text-sm text-gray-600 p-3 rounded-lg bg-blue-50 border border-blue-100">
                                <span className="font-medium">QuickBooks: </span>
                                {original && original.accountingLink
                                    ? 'Linked to an item in QuickBooks.'
                                    : 'Not linked to QuickBooks yet. When an invoice with this item syncs, QuickBooks uses its item with the same name, or creates one.'}
                            </p>
                        )}

                        {!creating && (
                            <div>
                                <button type="button" className={BTN_LINK} aria-expanded={hist.open}
                                    onClick={() => (hist.open ? setHist((h) => ({ ...h, open: false })) : loadMoreHistory(true))}>
                                    <History size={16} /> {hist.open ? 'Hide price history' : 'Show price history'}
                                </button>
                                {hist.open && (
                                    <div className="mt-2 space-y-2">
                                        {hist.entries.map((e) => (
                                            <div key={e.id} className="p-3 rounded-lg border border-gray-200 text-sm">
                                                <p className="text-gray-800">{historyChanges(e) || '—'}</p>
                                                <p className="text-xs text-gray-500 mt-0.5">{historyCause(e)}</p>
                                                <p className="text-xs text-gray-400 mt-0.5">{dateTime(e.at)}{e.byName ? ` · ${e.byName}` : ''}</p>
                                            </div>
                                        ))}
                                        {hist.loading && <p className="text-sm text-gray-500"><Loader2 size={14} className="inline animate-spin mr-2" />Loading…</p>}
                                        {!hist.loading && !hist.error && hist.entries.length === 0 && <p className="text-sm text-gray-500">No price changes yet.</p>}
                                        <ErrorLine text={hist.error} onRetry={() => loadMoreHistory(hist.entries.length === 0)} />
                                        {hist.last && !hist.loading && <button type="button" className={BTN_GHOST} onClick={() => loadMoreHistory(false)}>Show older</button>}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <footer className="px-4 sm:px-5 py-3 border-t border-gray-200 bg-white sticky bottom-0 space-y-2">
                        <ErrorLine text={error} />
                        <div className="flex flex-wrap items-center gap-2">
                            {!creating && original && (
                                <button type="button" className={original.archived ? BTN_GHOST : BTN_DANGER} disabled={saving} onClick={toggleArchive}>
                                    {original.archived ? 'Restore' : 'Archive'}
                                </button>
                            )}
                            <div className="flex-1" />
                            <button type="button" className={BTN_GHOST} disabled={saving} onClick={() => onClose({})}>Cancel</button>
                            <button type="button" className={BTN_PRIMARY} disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save'}</button>
                        </div>
                        <p className="text-xs text-gray-500">Saving changes the catalog only. Estimates and invoices already written keep their prices.</p>
                    </footer>
                </>
            )}
        </section>
    );
}
